import React, { useState, useEffect } from 'react';
import {
  Modal,
  BlockStack,
  Select,
  Button,
  InlineStack,
  Text,
  Box,
  Spinner,
  Banner
} from '@shopify/polaris';
import { usersService } from '../../services/usersService';
import { ticketsService } from '../../services/ticketsService';

const AssignTicketModal = ({
  isOpen,
  onClose,
  ticketId,
  currentAssignee = null,
  onSuccess,
  onError
}) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(currentAssignee ? currentAssignee.toString() : '');
  const [error, setError] = useState(null);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      // Set current assignee if provided
      if (currentAssignee) {
        setSelectedUserId(currentAssignee.toString());
      }
    }
  }, [isOpen, currentAssignee]);

  // Load assignable users
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const usersData = await usersService.getAssignableUsers();

      // Format users for Select component
      const formattedUsers = usersData.map(user => ({
        label: `${user.name} - ${user.group_name} (${user.email})`,
        value: user._id.toString()
      }));

      setUsers([
        { label: '-- Unassign --', value: '' },
        ...formattedUsers
      ]);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedUserId) {
      // Unassign - for now we'll just show a message
      // TODO: Implement unassign functionality if needed
      alert('Unassign functionality not yet implemented');
      return;
    }

    if (selectedUserId === currentAssignee?.toString()) {
      setError('Please select a different user');
      return;
    }

    setIsAssigning(true);
    setError(null);

    try {
      await ticketsService.assignTicket(ticketId, selectedUserId);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error assigning ticket:', error);
      const errorMessage = error.message || 'Failed to assign ticket';
      setError(errorMessage);

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isAssigning) {
      setSelectedUserId(currentAssignee ? currentAssignee.toString() : '');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>Assign Ticket</div>}
      primaryAction={{
        content: 'Assign',
        onAction: handleAssign,
        loading: isAssigning,
        disabled: isAssigning || isLoading || !selectedUserId
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: isAssigning
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Error Banner */}
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {/* Current Assignee Info */}
          {currentAssignee && (
            <Box>
              <Text variant="bodyMd" as="p" tone="subdued">
                Current assignee will be updated
              </Text>
            </Box>
          )}

          {/* Loading State */}
          {isLoading ? (
            <Box padding="400">
              <InlineStack align="center">
                <Spinner size="small" />
                <Text variant="bodyMd" as="span">Loading users...</Text>
              </InlineStack>
            </Box>
          ) : (
            /* User Selection */
            <Select
              label="Select User"
              options={users}
              value={selectedUserId}
              onChange={(value) => setSelectedUserId(value)}
              placeholder="Choose a user to assign"
              helpText="Select a user from the list to assign this ticket"
              disabled={isAssigning}
              requiredIndicator
            />
          )}

          {/* Help Text */}
          <Box>
            <Text variant="bodySm" as="p" tone="subdued">
              The selected user will be notified and the ticket will appear in their assigned tickets list.
            </Text>
          </Box>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default AssignTicketModal;
